/**
 * API service for Track Annotations
 * Allows users to annotate GPS track segments with tags, notes, and pass/fail status
 */

import axios from 'axios';

const baseUrl = process.env.REACT_APP_API_URL || '/api';

// ==================== Types ====================

export interface Annotation {
  id: number;
  course_id: string;
  entity_id: string;
  device_type?: string;
  start_time: string;
  end_time: string;
  geometry?: any;  // GeoJSON polygon
  center_latitude?: number;
  center_longitude?: number;
  route_component_type?: string;
  custom_tags: string[];
  status: 'pass' | 'fail' | 'pending' | 'review';
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at?: string;
}

export interface AnnotationCreate {
  course_id: string;
  entity_id: string;
  device_type?: string;
  start_time: string;
  end_time: string;
  geometry?: any;
  center_latitude?: number;
  center_longitude?: number;
  route_component_type?: string;
  custom_tags?: string[];
  status?: string;
  notes?: string;
}

export interface AnnotationUpdate {
  route_component_type?: string;
  custom_tags?: string[];
  status?: string;
  notes?: string;
  start_time?: string;
  end_time?: string;
  geometry?: any;
  center_latitude?: number;
  center_longitude?: number;
}

export interface AnnotationListResponse {
  items: Annotation[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface Tag {
  id: number;
  tag_name: string;
  tag_category?: string;
  usage_count: number;
  created_at: string;
}

export interface TagListResponse {
  items: Tag[];
  total: number;
}

export interface ChoiceLists {
  route_component_types: string[];
  statuses: string[];
  existing_tags: string[];
}

export interface AnnotationSearchParams {
  course_id?: string;
  entity_id?: string;
  status?: string;
  route_component_type?: string;
  tag?: string;
  created_by?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}

// ==================== Helper ====================

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`
});

// ==================== API Functions ====================

/**
 * Get dropdown choices for annotation form
 */
export const getChoiceLists = async (): Promise<ChoiceLists> => {
  const response = await axios.get(`${baseUrl}/annotations/choices`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Create a new annotation
 */
export const createAnnotation = async (annotation: AnnotationCreate): Promise<Annotation> => {
  const response = await axios.post(`${baseUrl}/annotations`, annotation, {
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * List annotations with optional filters
 */
export const listAnnotations = async (params: AnnotationSearchParams = {}): Promise<AnnotationListResponse> => {
  const response = await axios.get(`${baseUrl}/annotations`, {
    params,
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Get annotations for a specific course (convenience method)
 */
export const getAnnotationsForCourse = async (courseId: string): Promise<Annotation[]> => {
  const response = await listAnnotations({ course_id: courseId, page_size: 200 });
  return response.items;
};

/**
 * Get a specific annotation by ID
 */
export const getAnnotation = async (id: number): Promise<Annotation> => {
  const response = await axios.get(`${baseUrl}/annotations/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Update an existing annotation
 */
export const updateAnnotation = async (id: number, annotation: AnnotationUpdate): Promise<Annotation> => {
  const response = await axios.put(`${baseUrl}/annotations/${id}`, annotation, {
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Delete an annotation
 */
export const deleteAnnotation = async (id: number): Promise<void> => {
  await axios.delete(`${baseUrl}/annotations/${id}`, {
    headers: getAuthHeaders()
  });
};

/**
 * List all available tags
 */
export const listTags = async (category?: string, search?: string): Promise<TagListResponse> => {
  const response = await axios.get(`${baseUrl}/annotations/tags/all`, {
    params: { category, search },
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Create a new tag
 */
export const createTag = async (tagName: string, tagCategory?: string): Promise<Tag> => {
  const response = await axios.post(`${baseUrl}/annotations/tags`, {
    tag_name: tagName,
    tag_category: tagCategory
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== Constants ====================

export const ROUTE_COMPONENT_TYPES = [
  'SP',
  'CS',
  'TP',
  'VDO',
  'DP',
  'Abort',
  'PZ (N/S/E/W)',
  'PC',
  'OA',
  'LPT P/U',
  'LPT D/O',
  'SVLAR (N/S/E/W)',
  'CB (N/S/E/W)',
  'CG',
  'Drop',
  'Recover',
  'VDO/TWA',
  'TWA',
  'Other'
];

export const ANNOTATION_STATUSES = ['pass', 'fail', 'pending', 'review'];

export const STATUS_COLORS: Record<string, string> = {
  pass: '#4caf50',    // Green
  fail: '#f44336',    // Red
  pending: '#ff9800', // Orange
  review: '#2196f3'   // Blue
};

export default {
  getChoiceLists,
  createAnnotation,
  listAnnotations,
  getAnnotationsForCourse,
  getAnnotation,
  updateAnnotation,
  deleteAnnotation,
  listTags,
  createTag,
  ROUTE_COMPONENT_TYPES,
  ANNOTATION_STATUSES,
  STATUS_COLORS
};
